import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import React from "react";
import { createPluggable } from "pluggable-react";
import styles from "./Chip.module.scss";
import { withRefProp } from "../../../utils/withRefProp";

/** Chip variants use CSS custom properties from Chip.module.scss (e.g. --success-light, --success-light-foreground). */
export const chipVariants = {
  success: {
    color: "var(--success-light-foreground)",
    backgroundColor: "var(--success-light)",
  },
  destructive: {
    color: "var(--destructive-light-foreground)",
    backgroundColor: "var(--destructive-light)",
  },
  warning: {
    color: "var(--warning-light-foreground)",
    backgroundColor: "var(--warning-light)",
  },
  info: {
    color: "var(--info-light-foreground)",
    backgroundColor: "var(--info-light)",
  },
  subtle: {
    color: "var(--subtle-light-foreground)",
    backgroundColor: "var(--subtle-light)",
  },
  secondary: {
    color: "var(--secondary-light-foreground)",
    backgroundColor: "var(--secondary-light)",
  }
} as const;

export type ChipVariantKey = keyof typeof chipVariants;

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  color?: string;
  backgroundColor?: string;
  variant?: ChipVariantKey;
  size?: "sm" | "md" | "lg";
  outlined?: boolean;
  icon?: React.ReactNode;
  label?: React.ReactNode;
  /** Render the chip styles onto the single child element instead of a `<span>` (e.g. a `<button>` for a clickable chip). */
  asChild?: boolean;
  ref?: React.Ref<HTMLSpanElement>;
}

const DefaultChip = withRefProp((props: ChipProps) => {
  const { className, color, backgroundColor, variant = "secondary", size = "md", outlined = false, icon, label, asChild = false, style, children, ref, ...otherProps } = props;
  const Comp = asChild ? Slot : "span";
  const variantStyles = variant ? chipVariants[variant] : undefined;
  const effectiveColor = color ?? variantStyles?.color;
  const effectiveBackgroundColor = backgroundColor ?? variantStyles?.backgroundColor;
  const sizeToken = size;
  const content = children ?? (
    <>
      {icon ? <ChipIcon>{icon}</ChipIcon> : null}
      {label ? <ChipLabel>{label}</ChipLabel> : null}
    </>
  );
  return (
    <Comp
      ref={ref}
      className={clsx(styles.chip, className)}
      data-deci-chip="true"
      data-variant={variant}
      data-size={sizeToken}
      data-outlined={outlined || undefined}
      style={{
        ...style,
        color: effectiveColor,
        backgroundColor: effectiveBackgroundColor,
        borderColor: outlined ? effectiveColor : undefined,
      }}
      {...otherProps}
    >
      {content}
    </Comp>
  );
});

const Chip = createPluggable<ChipProps>("deci.chip", DefaultChip);

export interface ChipIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  ref?: React.Ref<HTMLSpanElement>;
}

const ChipIcon = withRefProp((props: ChipIconProps) => {
  const { className, ref, ...otherProps } = props;
  return <span ref={ref} className={clsx(styles.chipIcon, className)} {...otherProps} />;
});

export interface ChipLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  ref?: React.Ref<HTMLSpanElement>;
}

const ChipLabel = withRefProp((props: ChipLabelProps) => {
  const { className, ref, ...otherProps } = props;
  return <span ref={ref} className={clsx(styles.chipLabel, className)} {...otherProps} />;
});

export default Object.assign(Chip, {
  Icon: ChipIcon,
  Label: ChipLabel,
});
